import React, { useCallback } from "react";

function RadioButton({ 
  label, 
  options = [], 
  value, 
  onChange, 
  required = false,
  disabled = false,
  error = null,
  name,
  direction = "horizontal" 
}) {
  const handleChange = useCallback((optionValue) => {
    if (!disabled) {
      onChange(optionValue);
    }
  }, [disabled, onChange]);

  const handleKeyDown = useCallback((e, optionValue) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleChange(optionValue);
    }
  }, [handleChange]);

  const isHorizontal = direction === "horizontal";
  const hasError = !!error;
  const groupId = name || `radio-group-${label}`;

  return (
    <div style={styles.container}>
      {label && (
        <label style={styles.label} id={`${groupId}-label`}>
          {label}
          {required && <span style={styles.requiredStar}> *</span>}
        </label>
      )}
      
      <div 
        role="radiogroup"
        aria-labelledby={label ? `${groupId}-label` : undefined}
        aria-required={required}
        aria-invalid={hasError}
        style={{
          ...styles.group,
          flexDirection: isHorizontal ? "row" : "column",
          gap: isHorizontal ? "var(--space-lg)" : "var(--space-md)",
        }}
      >
        {options.map((option, index) => {
          const optionId = `${groupId}-option-${index}`;
          const isChecked = value === option;
          
          return (
            <label 
              key={index} 
              htmlFor={optionId}
              style={{
                ...styles.optionLabel,
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              <input
                id={optionId}
                type="radio"
                name={groupId}
                value={option}
                checked={isChecked}
                onChange={(e) => handleChange(e.target.value)}
                onKeyDown={(e) => handleKeyDown(e, option)}
                disabled={disabled}
                aria-checked={isChecked}
                style={{
                  ...styles.radio,
                  cursor: disabled ? "not-allowed" : "pointer",
                }}
              />
              <span style={styles.optionText}>{option}</span>
            </label>
          );
        })}
      </div>

      {/* Error message */}
      {hasError && (
        <p style={styles.errorMessage} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const styles = {
  container: {
    marginBottom: "var(--space-md)",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  label: {
    color: "var(--text-main)",
    fontSize: "var(--font-size-sm)",
    fontWeight: "var(--font-weight-semibold)",
    marginBottom: "var(--space-sm)",
    fontFamily: "var(--font-family-sans)",
  },
  requiredStar: {
    color: "var(--error)",
  },
  group: {
    display: "flex",
    flexWrap: "wrap",
  },
  optionLabel: {
    display: "flex",
    alignItems: "center",
    gap: "var(--space-sm)",
    transition: "opacity var(--transition-fast)",
    userSelect: "none",
  },
  optionText: {
    color: "var(--text-muted)",
    fontSize: "var(--font-size-md)",
    fontWeight: "var(--font-weight-normal)",
    fontFamily: "var(--font-family-sans)",
  },
  radio: {
    accentColor: "var(--primary)",
    width: "18px",
    height: "18px",
    minWidth: "18px",
    minHeight: "18px",
    margin: 0,
    transition: "opacity var(--transition-fast)",
  },
  errorMessage: {
    color: "var(--error)",
    fontSize: "var(--font-size-sm)",
    fontWeight: "var(--font-weight-normal)",
    marginTop: "var(--space-xs)",
    fontFamily: "var(--font-family-sans)",
  },
};

export default RadioButton;